import { useEffect, useRef, useState, useCallback } from 'preact/hooks';
import type { ProfileDepth } from '../api/profile';

// ── Types ────────────────────────────────────────────────────

export interface GuideUserState {
	tier: 'free' | 'pro';
	emailVerified: boolean;
	hasCompletedExtraction: boolean;
	sessionCount: number;
	completedSessionCount: number;
	isPublished: boolean;
	profileIsStale: boolean;
	hasActiveSession: boolean;
	canStartInterview: boolean;
	/** Null while loading, never null once the profile response resolves. */
	profileDepth: ProfileDepth | null;
}

interface GuideCompanionProps {
	page: 'landing' | 'dashboard';
	userState?: GuideUserState;
	dismissible?: boolean;
}

// ── Content Maps ─────────────────────────────────────────────

const LANDING_QUIPS: Record<string, string> = {
	hero: "Bullet points are what's left after the interesting parts get cut. I'm here for the interesting parts.",
	process: "You do the talking. I take notes, make sense of them, and handle the questions when you're not in the room.",
	conversation: "No forms. You talk about what you did, I figure out the structure. Turns out that works better.",
	tiers: "Free gets you all of me — interview, narrative, profile, agent. Paid gets you more of me: unlimited sessions, a deeper narrative, and intel on the companies you've worked with.",
	'cta-footer': "Fifteen minutes. One conversation. I'll take it from there.",
};

function getDashboardStateQuip(state: GuideUserState): { key: string; message: string } {
	if (!state.emailVerified) {
		return { key: 'email-unverified', message: "Verify your email first. I'll wait." };
	}
	if (state.hasActiveSession) {
		return { key: 'active-session', message: "You left a conversation open. I've been thinking about where we were." };
	}
	if (state.sessionCount === 0) {
		return {
			key: 'no-sessions',
			message: "Hit Update Record and tell me what you do. I'll ask the follow-ups.",
		};
	}

	// Published states take priority over depth-based messaging — if you're
	// already live, the guide's job is to tell you about your live agent,
	// not grade your record.
	if (state.isPublished && state.profileIsStale) {
		return {
			key: 'published-stale',
			message: "Your record's changed since you last published. I'm out there with the old version.",
		};
	}
	if (state.isPublished) {
		return {
			key: 'published',
			message: "You're live. I'm representing you to visitors right now. I stick to what you told me — nothing more.",
		};
	}

	// Depth-driven ladder for unpublished users who have at least one session.
	// Depth may be null briefly while the profile response is in flight — fall
	// back to the generic "building" quip so the bubble never blanks.
	const depth = state.profileDepth;
	if (!depth) {
		return {
			key: 'building',
			message: "Keep going. Every conversation gives me a better way to explain what you actually do.",
		};
	}

	switch (depth.tier) {
		case 'below':
			return {
				key: 'depth-below',
				message: depth.floorReasons[0]
					? `${depth.floorReasons[0]} Another session and I'll have what I need.`
					: "I don't quite have enough to represent you yet. Keep going — tell me a place you worked and something you did there.",
			};
		case 'thin':
			return {
				key: 'depth-thin',
				message: "I can represent you now — just thinly. I'd be speaking from a sketch. Another session or two and I'll have the full picture.",
			};
		case 'developed':
			return {
				key: 'depth-developed',
				message: "I know enough to represent you well. Hit Publish when you're ready to let me loose.",
			};
		case 'rich':
			return {
				key: 'depth-rich',
				message: "I've got a lot to work with — range across your career, not just highlights. Hit Publish when you're ready.",
			};
	}
}

const DASHBOARD_SECTION_QUIPS: Record<string, string> = {
	sessions: "Your conversations with me. I keep all of them — sometimes the third time you explain something is when it clicks.",
	reports: "What visitors asked about you. The questions tell you more than the answers do.",
};

// ── Hook: useGuideContext ────────────────────────────────────

function useGuideContext(
	page: 'landing' | 'dashboard',
	userState?: GuideUserState,
): { message: string; contextKey: string } {
	const [visibleSection, setVisibleSection] = useState<string | null>(null);
	const [hoveredSection, setHoveredSection] = useState<string | null>(null);
	const ratioMap = useRef<Map<string, number>>(new Map());
	const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

	const updateSection = useCallback(() => {
		if (page === 'landing') {
			// Landing: pick last visible section in DOM order.
			// Tall sections would otherwise dominate by ratio.
			const sections = document.querySelectorAll('[data-guide-section]');
			let lastVisible: string | null = null;
			for (const section of sections) {
				const id = (section as HTMLElement).dataset.guideSection!;
				const ratio = ratioMap.current.get(id) || 0;
				if (ratio > 0.05) {
					lastVisible = id;
				}
			}
			if (lastVisible) {
				setVisibleSection(lastVisible);
			}
		} else {
			// Dashboard: pick section with highest ratio.
			// Stacked mobile layout makes this correct; cursor
			// hover handles the side-by-side desktop case.
			let best: string | null = null;
			let bestRatio = 0;
			for (const [id, ratio] of ratioMap.current) {
				if (ratio > bestRatio) {
					bestRatio = ratio;
					best = id;
				}
			}
			if (best && bestRatio > 0.1) {
				setVisibleSection(best);
			}
		}
	}, [page]);

	// Scroll-based detection (IntersectionObserver)
	useEffect(() => {
		const sections = document.querySelectorAll('[data-guide-section]');
		if (!sections.length) return;

		const observer = new IntersectionObserver(
			(entries) => {
				for (const entry of entries) {
					const id = (entry.target as HTMLElement).dataset.guideSection!;
					ratioMap.current.set(id, entry.intersectionRatio);
				}
				if (debounceTimer.current) clearTimeout(debounceTimer.current);
				debounceTimer.current = setTimeout(updateSection, 200);
			},
			{ threshold: [0, 0.15, 0.3, 0.5, 0.7, 1] },
		);

		sections.forEach((s) => observer.observe(s));
		return () => {
			observer.disconnect();
			if (debounceTimer.current) clearTimeout(debounceTimer.current);
		};
	}, [page, updateSection]);

	// Cursor-based detection (mouseenter/mouseleave on sections)
	useEffect(() => {
		const sections = document.querySelectorAll('[data-guide-section]');
		if (!sections.length) return;

		const handlers = new Map<Element, { enter: () => void; leave: () => void }>();
		for (const section of sections) {
			const id = (section as HTMLElement).dataset.guideSection!;
			const enter = () => setHoveredSection(id);
			const leave = () => setHoveredSection((prev) => prev === id ? null : prev);
			section.addEventListener('mouseenter', enter);
			section.addEventListener('mouseleave', leave);
			handlers.set(section, { enter, leave });
		}

		return () => {
			for (const [el, { enter, leave }] of handlers) {
				el.removeEventListener('mouseenter', enter);
				el.removeEventListener('mouseleave', leave);
			}
		};
	}, [page]);

	// Resolve: hover > scroll > default
	const activeSection = hoveredSection || visibleSection;

	// Landing page: section quip only
	if (page === 'landing') {
		const section = activeSection || 'hero';
		const message = LANDING_QUIPS[section] || LANDING_QUIPS.hero;
		return { message, contextKey: `landing-${section}` };
	}

	// Dashboard: hover > scroll section > state
	if (userState) {
		const stateQuip = getDashboardStateQuip(userState);

		// Hovered or scrolled-to section (non-profile) shows section quip
		const sectionKey = activeSection;
		if (sectionKey && sectionKey !== 'profile' && DASHBOARD_SECTION_QUIPS[sectionKey]) {
			return {
				message: DASHBOARD_SECTION_QUIPS[sectionKey],
				contextKey: `dashboard-section-${sectionKey}`,
			};
		}

		return { message: stateQuip.message, contextKey: `dashboard-state-${stateQuip.key}` };
	}

	return { message: '', contextKey: 'empty' };
}

// ── Component ────────────────────────────────────────────────

const STORAGE_KEY = 'pxtxt-guide-dismissed';

export function GuideCompanion({ page, userState, dismissible }: GuideCompanionProps) {
	const { message, contextKey } = useGuideContext(page, userState);
	const [dismissed, setDismissed] = useState(() => {
		if (!dismissible) return false;
		try { return localStorage.getItem(STORAGE_KEY) === '1'; } catch { return false; }
	});
	const [displayMessage, setDisplayMessage] = useState(message);
	const [fading, setFading] = useState(false);
	const prevKey = useRef(contextKey);

	// Animate text transitions
	useEffect(() => {
		if (contextKey === prevKey.current) {
			// First render or same context — just set directly
			setDisplayMessage(message);
			return;
		}
		prevKey.current = contextKey;
		setFading(true);
		const timer = setTimeout(() => {
			setDisplayMessage(message);
			setFading(false);
		}, 150);
		return () => clearTimeout(timer);
	}, [contextKey, message]);

	const handleDismiss = useCallback(() => {
		setDismissed(true);
		try { localStorage.setItem(STORAGE_KEY, '1'); } catch { /* noop */ }
	}, []);

	const handleRestore = useCallback(() => {
		setDismissed(false);
		try { localStorage.removeItem(STORAGE_KEY); } catch { /* noop */ }
	}, []);

	if (!message) return null;

	return (
		<div class={`guide-companion guide-companion--${page}`} aria-live="polite">
			{/* Speech bubble */}
			{!dismissed && (
				<div class={`guide-bubble ${fading ? 'guide-bubble--fading' : ''}`}>
					<p class="guide-bubble__text">{displayMessage}</p>
					{dismissible && (
						<button
							class="guide-bubble__dismiss"
							onClick={handleDismiss}
							aria-label="Dismiss guide"
							type="button"
						>
							<svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" stroke-width="1.5">
								<path d="M2 2l6 6M8 2l-6 6" />
							</svg>
						</button>
					)}
					{/* Tail — right triangle from bottom-right corner */}
					{/* White rect at top covers the inset box-shadow junction line */}
					<svg class="guide-bubble__tail" width="10" height="11" viewBox="0 -1 10 11" fill="none" xmlns="http://www.w3.org/2000/svg">
						<rect x="0" y="-1" width="10" height="2" fill="white" />
						<path d="M0 0H10V10Z" fill="white" />
						<path d="M0 0L10 10" stroke="#222222" stroke-width="1" />
						<line x1="9.5" y1="-1" x2="9.5" y2="10" stroke="#222222" stroke-width="1" />
					</svg>
				</div>
			)}

			{/* Avatar — positioned to the right of the bubble */}
			<button
				class={`guide-avatar ${dismissed ? 'guide-avatar--dimmed' : ''}`}
				onClick={dismissed ? handleRestore : undefined}
				aria-label={dismissed ? 'Show guide' : 'pxtxt guide'}
				type="button"
				style={dismissed ? { cursor: 'pointer' } : { cursor: 'default' }}
			>
				<img src="/pxtxt-avatar.svg" alt="" width="40" height="40" />
			</button>

			<style>{`
				/* ── Guide Companion ───────────────────── */
				/* Layout: bubble above-left, avatar below-right.         */
				/* The tail connects them — it extends from the bubble's  */
				/* bottom-right corner down toward the avatar.            */

				.guide-companion {
					position: fixed;
					bottom: 4rem;
					right: 3rem;
					z-index: 200;
					display: flex;
					flex-direction: column;
					align-items: flex-end;
					pointer-events: none;
				}

				.guide-companion > * {
					pointer-events: auto;
				}

				/* ── Speech Bubble ─────────────────────── */
				/* Matches quip_bubble.svg design language:               */
				/* 1px #222 border, 8px radius (except bottom-right      */
				/* where tail connects), white fill, offset shadow.       */

				.guide-bubble {
					position: relative;
					max-width: 260px;
					padding: 14px 18px;
					background: white;
					border: none;
					box-shadow: inset 0 0 0 1px #222222;
					border-radius: 8px 8px 0 8px;
					filter: drop-shadow(2px 2px 0 rgba(0, 0, 0, 0.16));
					margin-right: 40px;
					opacity: 1;
					transform: translateY(0);
					transition: opacity 150ms var(--ease-out-quart),
					            transform 200ms var(--ease-out-expo);
				}

				.guide-bubble--fading {
					opacity: 0;
					transform: translateY(4px);
				}

				/* Tail: right-triangle flush with bottom-right corner.    */
				/* No border = no junction artifact. box-shadow inset      */
				/* handles the bubble outline, tail SVG continues it.      */
				.guide-bubble__tail {
					position: absolute;
					top: calc(100% - 1px);
					right: 0;
					display: block;
				}

				.guide-bubble__text {
					margin: 0;
					font-family: var(--font-mono);
					font-size: 12px;
					line-height: 18px;
					color: #222222;
				}

				.guide-bubble__dismiss {
					position: absolute;
					top: 8px;
					right: 6px;
					width: 16px;
					height: 16px;
					display: flex;
					align-items: center;
					justify-content: center;
					background: none;
					border: none;
					padding: 0;
					cursor: pointer;
					color: #999;
					transition: color 150ms;
				}

				.guide-bubble__dismiss:hover {
					color: #222;
				}

				/* ── Avatar ────────────────────────────── */
				/* Sits below the tail. Center-x aligns with bubble's     */
				/* right edge (bubble has margin-right: 20px = half avatar) */

				.guide-avatar {
					width: 40px;
					height: 40px;
					border-radius: 50%;
					background: none;
					border: none;
					padding: 0;
					display: flex;
					align-items: center;
					justify-content: center;
					transition: opacity 200ms var(--ease-out-quart);
					margin-top: 12px;
				}

				.guide-avatar img {
					width: 40px;
					height: 40px;
					border-radius: 50%;
				}

				.guide-avatar--dimmed {
					opacity: 0.4;
				}

				.guide-avatar--dimmed:hover {
					opacity: 0.7;
				}

				/* ── Mobile ────────────────────────────── */

				@media (max-width: 767px) {
					.guide-companion {
						bottom: 5rem;
						right: 1.25rem;
					}

					.guide-bubble {
						max-width: 200px;
						margin-right: 32px;
					}

					.guide-avatar {
						width: 32px;
						height: 32px;
						margin-top: 10px;
					}

					.guide-avatar img {
						width: 32px;
						height: 32px;
					}
				}
			`}</style>
		</div>
	);
}
